/* Shown at the foot of the mobile More sheet, so someone reporting a problem can
   say which build they're on. Set REACT_APP_VERSION in the build environment
   (CRA inlines it) — the fallback below is only for local runs. */
export const APP_VERSION = process.env.REACT_APP_VERSION || 'v2026-09-16.1';

/* reCAPTCHA v2 checkbox, required by the OTP-send API (first send and resends).
   Site keys are public; set the real one in .env (REACT_APP_RECAPTCHA_SITE_KEY).
   The fallback is Google's universal TEST key — it always passes, dev only. */
export const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

export const DIET_TAGS = [
  { id: 'veg', label: 'Vegetarian', icon: '🥕' },
  { id: 'vegan', label: 'Vegan', icon: '🌱' },
  { id: 'prek', label: 'Pre-K', icon: '🧒' },
  { id: 'gf', label: 'Gluten Free', icon: '🌾' },
  { id: 'local', label: 'Local Ingredients', icon: '🍅' },
];

export const DAY_NAMES = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday' };

/* The interest list is no longer hard-coded here — it comes from
   /user/getActivity so the tags this portal writes match the phone app's. */

/* api = the enum the donation API accepts: venmo, applePay, check, cash, other */
export const DONATION_SOURCES = [
  { value: 'Venmo', label: 'Venmo', api: 'venmo' },
  { value: 'Apple Pay', label: 'Apple Pay', api: 'applePay' },
  { value: 'Check', label: 'Check', api: 'check' },
  { value: 'Cash', label: 'Cash', api: 'cash' },
  { value: 'Other', label: 'Other', api: 'other' },
];

export function donationSourceLabel(apiValue) {
  const s = DONATION_SOURCES.find((x) => x.api === apiValue || x.value === apiValue);
  return s ? s.label : String(apiValue || '');
}

/* Ways a parent can send money to the class fund — the Links page's Donation
   Links. `id` is the enum the donationMethod API accepts (zelle, venmo,
   applePay, other). The rest is what the form and the tiles show: `icon` is a
   brand-coloured badge recreation in public/assets/icons (the client's
   originals were not provided), and each rail names its own handle field,
   because what a parent needs differs per rail. `handleType` is sent to the
   API when we already know the kind; otherwise the server detects it from the
   handle itself. The form shows the recipient's name only for a rail that has
   `recipientHelp` — "Other" is just the handle or link. */
export const DONATION_METHODS = [
  {
    id: 'zelle',
    label: 'Zelle',
    icon: '/assets/icons/zelle.svg',
    fieldLabel: 'Phone number or email',
    placeholder: '(512) 555-0147  or  you@email.com',
    help: 'Whatever is registered with Zelle at your bank. Zelle has no username, so this is what a parent types into their own banking app.',
    recipientHelp: 'Zelle doesn’t show parents a name when they send — this is whose account it is.',
    tileSub: 'Parents copy this into their own app',
  },
  {
    id: 'venmo',
    label: 'Venmo',
    icon: '/assets/icons/venmo.svg',
    fieldLabel: '@handle',
    placeholder: '@roomparent-taylor',
    help: 'Your Venmo username. This is the only method with a page a parent can open directly — the link is built from the handle, so there is nothing to keep in sync.',
    recipientLabel: 'Name on the Venmo account',
    recipientHelp: 'Parents check this against the name Venmo shows them.',
    tileSub: 'Parents search this in Venmo',
    handleType: 'username',
    /* The first time a parent pays someone new, Venmo asks for the last 4
       digits of that person's phone — only Venmo has this field */
    last4Label: 'Last 4 digits of the phone',
    last4Placeholder: '0147',
    last4Help: 'Venmo asks for this when a parent pays you for the first time.',
  },
  {
    id: 'applePay',
    label: 'Apple Pay',
    icon: '/assets/icons/applepay.svg',
    fieldLabel: 'Phone number or email',
    placeholder: '(512) 555-0147  or  you@icloud.com',
    help: 'The phone number or email on your Apple Cash account — parents send to it from Messages or Wallet.',
    recipientHelp: 'Whose Apple Cash account this is, so parents know who they’re sending to.',
    tileSub: 'Parents send to this from Apple Cash',
  },
  {
    id: 'other',
    label: 'Other',
    icon: '/assets/icons/donation.svg',
    fieldLabel: 'Handle or link',
    placeholder: 'A username, a number, or a full URL',
    help: 'Anything else your class uses. Parents see exactly what you type here.',
    tileSub: 'Parents copy this into their own app',
  },
];

export const donationMethodOf = (id) =>
  DONATION_METHODS.find((m) => m.id === id) || DONATION_METHODS[DONATION_METHODS.length - 1];

/* What a saved method is called on screen: "other" shows the name the room
   parent gave it, everything else its own brand name. */
export const donationMethodLabel = (row) =>
  (row?.method === 'other' && row?.methodName) || donationMethodOf(row?.method).label;

export const FIXED_REASON_TAGS = [
  { id: 'supplies', label: 'Supplies' },
  { id: 'teachergift', label: 'Teacher Gift' },
  { id: 'other', label: 'Other' },
];

/* Donation reasons / expense tags = the fixed tags + every class event.
   Seeded demo events (seed: true) are excluded — donations/expenses persist to
   the real API, so only this class's real events belong as reasons. Once the
   events module is API-backed, its events appear here automatically. */
export function reasonTagOptions(events) {
  const classEvents = events.filter((e) => e.scope === 'class' && !e.seed);
  return [...FIXED_REASON_TAGS, ...classEvents.map((e) => ({ id: String(e.id), label: e.title }))];
}

export function reasonTagLabel(tagId, events) {
  const t = reasonTagOptions(events).find((x) => x.id === String(tagId));
  return t ? t.label : 'Supplies';
}

/* Same as the display label now that labels carry no icon prefix. */
export function reasonPlainLabel(tagId, events) {
  return reasonTagLabel(tagId, events);
}
